import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  House as FaHome,
  Box as FaBoxOpen,
  ClipboardData as FaClipboardList,
  Cart as FaShoppingCart,
  People as FaUserFriends,
  People as FaUsers,
  Building as FaBuilding,
  Gear as FaCogs,
  BoxArrowRight as FaSignOutAlt,
  ChevronLeft as FaChevronLeft,
  ChevronDown as FaChevronDown,
  ArrowCounterclockwise as FaReturns,
  FileEarmarkText as FaFileText,
  Wallet2 as FaWallet2,
  CreditCard2Front as FaCreditCard,
  CashStack as FaCashStack,
  BagCheck as FaBagCheck,
  Truck as FaTruck,
  Receipt as FaReceipt,
  JournalText as FaQuotes,
} from 'react-bootstrap-icons';
import SideNavGroup from './SideNavGroup';
import {
  AdminNavItem,
  getAdminNavItems,
  isNavGroup,
  NAV_GROUP_PATH_PREFIXES,
} from './adminNavItems';
import {
  Nav,
  Dropdown,
  Image,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
  Badge,
} from 'react-bootstrap';
import Logo from '../../res/MayorX.svg';
import { signOut } from '../../api/auth';
import { useBranding } from '../../contexts/BrandingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Organization as OrgModel } from '../../models';
import styles from './SideNav.module.scss';

const useOutsideClick = (ref: React.RefObject<any>, handler: () => void) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

interface SideNavProps {
  setStatus: (status: 'expanded' | 'shrinked') => void;
  organization?: OrgModel | null;
  userOrganizations?: OrgModel[];
  onOrganizationChange?: (organizationId: string) => void;
  isAdmin?: boolean;
  panelPermissions?: string[] | null;
  isGodMode?: boolean;
}

const NAV_ICONS: { [key: string]: JSX.Element } = {
  home: <FaHome />,
  inventory: <FaBoxOpen />,
  orders: <FaClipboardList />,
  'orders-group': <FaFileText />,
  quotes: <FaQuotes />,
  returns: <FaReturns />,
  'clients-orders': <FaShoppingCart />,
  vendors: <FaUserFriends />,
  'client-users': <FaUsers />,
  clients: <FaBuilding />,
  'finance-group': <FaCashStack />,
  'accounts-receivable': <FaWallet2 />,
  'accounts-payable': <FaCreditCard />,
  'purchasing-group': <FaBagCheck />,
  suppliers: <FaTruck />,
  purchases: <FaReceipt />,
  configuration: <FaCogs />,
};

const SideNav: React.FC<SideNavProps> = ({
  setStatus,
  organization,
  userOrganizations = [],
  onOrganizationChange,
  isAdmin = false,
  panelPermissions = null,
  isGodMode = false,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [groupMenuOpen, setGroupMenuOpen] = useState<Record<string, boolean>>({
    orders: true,
    finance: true,
    purchasing: true,
  });
  const [collapsedDropdownGroup, setCollapsedDropdownGroup] = useState<
    string | null
  >(null);
  const location = useLocation();
  const navigate = useNavigate();
  const orgMenuRef = useRef(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const [navScrollFades, setNavScrollFades] = useState({
    top: false,
    bottom: false,
  });
  const { branding } = useBranding();
  const { theme, toggleTheme } = useTheme();

  const hoverBg = 'var(--bs-tertiary-bg)';
  const secondaryText = 'var(--bs-secondary-color)';
  const bodyText = 'var(--bs-body-color)';
  const primary = 'var(--bs-primary)';
  const onPrimary = 'var(--bs-primary-contrast)';

  useOutsideClick(orgMenuRef, () => {});

  useEffect(() => {
    setStatus(isExpanded ? 'expanded' : 'shrinked');
    if (isExpanded) {
      setCollapsedDropdownGroup(null);
    }
  }, [isExpanded, setStatus]);

  const handleToggle = () => setIsExpanded(!isExpanded);

  const handleLogout = async () => {
    try {
      await signOut();
      console.log('Signing out...');
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const nextOpen: Record<string, boolean> = { ...groupMenuOpen };
    Object.entries(NAV_GROUP_PATH_PREFIXES).forEach(([groupKey, prefixes]) => {
      if (prefixes.some(prefix => location.pathname.startsWith(prefix))) {
        nextOpen[groupKey] = true;
      }
    });
    setGroupMenuOpen(nextOpen);
    setCollapsedDropdownGroup(null);
  }, [location.pathname]);

  const navItems: AdminNavItem[] = getAdminNavItems(
    isAdmin,
    panelPermissions,
    isGodMode
  );

  const updateNavScrollFades = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const hasOverflow = scrollHeight > clientHeight + 1;
    setNavScrollFades({
      top: hasOverflow && scrollTop > 4,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - 4,
    });
  }, []);

  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    updateNavScrollFades();
    el.addEventListener('scroll', updateNavScrollFades, { passive: true });
    const resizeObserver = new ResizeObserver(updateNavScrollFades);
    resizeObserver.observe(el);
    const navContent = el.firstElementChild;
    if (navContent) resizeObserver.observe(navContent);
    return () => {
      el.removeEventListener('scroll', updateNavScrollFades);
      resizeObserver.disconnect();
    };
  }, [
    updateNavScrollFades,
    isExpanded,
    groupMenuOpen,
    navItems.length,
    organization?.id,
  ]);

  const navColors = {
    primary,
    onPrimary,
    hoverBg,
    secondaryText,
    bodyText,
  };

  return (
    <div
      className={`${styles.shell} min-w-0 ${
        isExpanded ? 'sidenav-expanded' : 'sidenav-shrinked'
      }`}
      style={{
        width: isExpanded ? '280px' : '88px',
        transition: 'width 0.3s ease-in-out',
      }}
    >
      <div
        className={`${styles.root} bg-body border-end min-w-0`}
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderRight: '1px solid var(--bs-border-color)',
        }}
      >
        {/* Header — same box height for org mark and default MayorX */}
        <div
          className={`border-bottom flex-shrink-0 ${styles.logoHeader} ${
            isExpanded ? '' : styles.logoHeaderCollapsed
          }`}
        >
          {branding.logo ? (
            <Image
              key={`logo-${branding.logo}`}
              src={branding.logo}
              alt="Logo"
              className={
                isExpanded
                  ? styles.brandLogoExpanded
                  : styles.brandLogoCollapsed
              }
            />
          ) : (
            <Image
              key={`logo-default`}
              src={Logo}
              alt="MayorX"
              className={`official-logo ${
                isExpanded
                  ? styles.defaultLogoExpanded
                  : styles.defaultLogoCollapsed
              }`}
            />
          )}
        </div>

        {/* Organization Selector */}
        {organization && (
          <div
            className="px-2 py-2 border-bottom min-w-0 flex-shrink-0"
            ref={orgMenuRef}
            style={{ overflowX: 'clip', overflowY: 'visible' }}
          >
            {isExpanded ? (
              <Dropdown className="w-100 min-w-0">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="w-100 d-flex justify-content-between align-items-center border-0 gap-2 min-w-0 text-start"
                >
                  <span
                    className="text-truncate fw-bold min-w-0 flex-grow-1"
                    title={organization.name}
                  >
                    {organization.name}
                  </span>
                  {organization.isSandbox && (
                    <Badge
                      bg="secondary"
                      className="flex-shrink-0 fw-normal"
                      style={{
                        fontSize: '0.65rem',
                        letterSpacing: '0.1em',
                      }}
                    >
                      PRUEBAS
                    </Badge>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="w-100"
                  style={{
                    maxWidth: '100%',
                    border: '1px solid var(--bs-border-color)',
                    boxShadow: 'none',
                  }}
                >
                  {userOrganizations.map(org => (
                    <Dropdown.Item
                      key={org.id}
                      active={org.id === organization.id}
                      className="d-flex align-items-center gap-2 min-w-0"
                      onClick={() =>
                        onOrganizationChange && onOrganizationChange(org.id)
                      }
                    >
                      <span
                        className="text-truncate flex-grow-1 min-w-0 text-start"
                        title={org.name}
                      >
                        {org.name}
                      </span>
                      {org.isSandbox && (
                        <Badge
                          bg="secondary"
                          pill
                          className="flex-shrink-0 fw-normal"
                          style={{
                            fontSize: '0.65rem',
                            letterSpacing: '0.06em',
                          }}
                        >
                          Pruebas
                        </Badge>
                      )}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <div
                title={organization.name}
                className="bg-secondary-light rounded-circle mx-auto d-flex align-items-center justify-content-center"
                style={{
                  width: '48px',
                  height: '48px',
                  boxShadow: organization.isSandbox
                    ? '0 0 0 1px rgba(148, 163, 184, 0.5)'
                    : undefined,
                }}
              >
                <span className="h5 text-primary fw-bold mb-0">
                  {organization.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Navigation — scrollable middle; footer stays pinned */}
        <div className={styles.navScrollWrap}>
          <div ref={navScrollRef} className={styles.navScroll}>
            <Nav className="flex-column p-2">
              {navItems.map((item, index) => {
                if (isNavGroup(item)) {
                  const groupKey = item.groupKey;
                  return (
                    <React.Fragment key={groupKey}>
                      <SideNavGroup
                        item={item}
                        isSidebarExpanded={isExpanded}
                        menuOpen={groupMenuOpen[groupKey] ?? true}
                        onToggleMenu={() =>
                          setGroupMenuOpen(prev => ({
                            ...prev,
                            [groupKey]: !(prev[groupKey] ?? true),
                          }))
                        }
                        dropdownOpen={collapsedDropdownGroup === groupKey}
                        onToggleDropdown={() =>
                          setCollapsedDropdownGroup(prev =>
                            prev === groupKey ? null : groupKey
                          )
                        }
                        onCloseDropdown={() => setCollapsedDropdownGroup(null)}
                        navIcons={NAV_ICONS}
                        colors={navColors}
                      />
                      {index < navItems.length - 1 && (
                        <div
                          style={{
                            borderTop: '1px solid var(--bs-border-color)',
                            margin: '0.35rem 0.5rem',
                            opacity: 0.7,
                          }}
                          aria-hidden
                        />
                      )}
                    </React.Fragment>
                  );
                }

                const isActive =
                  item.path && location.pathname.startsWith(item.path);
                return (
                  <React.Fragment key={item.path || item.text}>
                    <Nav.Item className="mb-1">
                      <Nav.Link
                        onClick={() => item.path && navigate(item.path)}
                        className={`d-flex align-items-center rounded-3 p-2 ${
                          !isExpanded ? 'justify-content-center' : ''
                        } ${
                          isActive
                            ? 'text-white'
                            : 'text-dark hover-bg-light'
                        }`}
                        style={{
                          ...(!isActive
                            ? {}
                            : {
                                backgroundColor: primary,
                                color: onPrimary,
                              }),
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: isActive ? '600' : '500',
                        }}
                        title={isExpanded ? '' : item.text}
                        onMouseEnter={e => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = hoverBg;
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }
                        }}
                      >
                        <span
                          className={`${isExpanded ? 'me-2' : ''} fs-5`}
                          style={{
                            color: isActive ? onPrimary : secondaryText,
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {NAV_ICONS[item.icon]}
                        </span>
                        {isExpanded && (
                          <span
                            className="fw-medium"
                            style={{
                              color: isActive ? onPrimary : bodyText,
                              transition: 'color 0.2s ease',
                            }}
                          >
                            {item.text}
                          </span>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    {index < navItems.length - 1 && (
                      <div
                        style={{
                          borderTop: '1px solid var(--bs-border-color)',
                          margin: '0.35rem 0.5rem',
                          opacity: 0.7,
                        }}
                        aria-hidden
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Nav>
          </div>
          <div
            className={`${styles.fade} ${styles.fadeTop} ${
              navScrollFades.top ? styles.fadeVisible : ''
            }`}
            aria-hidden
          />
          <div
            className={`${styles.fade} ${styles.fadeBottom} ${
              navScrollFades.bottom ? styles.fadeVisible : ''
            }`}
            aria-hidden
          />
          <div
            className={`${styles.scrollHint} ${
              navScrollFades.bottom ? styles.scrollHintVisible : ''
            }`}
            aria-hidden
          >
            <FaChevronDown className={styles.scrollHintIcon} />
          </div>
        </div>

        {/* Footer / Theme + Logout */}
        <div
          className="px-2 py-2 border-top flex-shrink-0"
          style={{ borderTopColor: 'var(--bs-border-color)' }}
        >
          <div
            className={`d-flex align-items-center gap-1 ${
              isExpanded ? 'justify-content-between' : 'flex-column'
            }`}
          >
            {isExpanded ? (
              <Form.Check
                type="switch"
                id="theme-switch"
                label="Modo oscuro"
                checked={theme === 'dark'}
                onChange={toggleTheme}
                className="mb-0 small"
              />
            ) : (
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="theme-tooltip">Cambiar tema</Tooltip>}
              >
                <Button
                  variant="link"
                  size="sm"
                  className="d-flex align-items-center justify-content-center p-1 text-secondary text-decoration-none"
                  onClick={toggleTheme}
                  aria-label="Cambiar tema"
                >
                  {theme === 'dark' ? '☾' : '☀︎'}
                </Button>
              </OverlayTrigger>
            )}
            {isExpanded ? (
              <Button
                variant="link"
                size="sm"
                className="d-flex align-items-center text-danger text-decoration-none px-1 py-0"
                onClick={handleLogout}
                aria-label="Cerrar Sesión"
              >
                <FaSignOutAlt className="me-1" size={14} />
                <span className="small">Cerrar Sesión</span>
              </Button>
            ) : (
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="logout-tooltip">Cerrar Sesión</Tooltip>
                }
              >
                <Button
                  variant="link"
                  size="sm"
                  className="d-flex align-items-center justify-content-center text-danger text-decoration-none p-1"
                  onClick={handleLogout}
                  aria-label="Cerrar Sesión"
                >
                  <FaSignOutAlt size={14} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      {/* Toggle — outside overflow:hidden root so it isn't clipped */}
      <Button
        variant={theme === 'dark' ? 'outline-secondary' : 'light'}
        onClick={handleToggle}
        className={`${styles.toggle} rounded-circle`}
      >
        <div
          style={{
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <FaChevronLeft />
        </div>
      </Button>
    </div>
  );
};

export default SideNav;
